import { mapFromEntities } from 'mappings/activityMapping';
import {
  mapFromFormKPIPlacementResult,
  mapFromTextCorrectionImpactPreview,
  mapToFormKPIPlacementMutation,
} from 'mappings/formVersionMapping';
import {
  mapFromKPI,
  mapFromKPIAlert,
  mapFromKPIContext,
  mapFromKPIDataPoint,
  mapFromKPIDataTransform,
  mapFromKPIDataTransformLogs,
  mapFromKPIDataTransformResponses,
  mapFromKPIs,
  mapToKPI,
  mapToKPIAlert,
  mapToKPIData,
  mapToKPIDataTransform,
  mapToKPIGraphDataConfig,
  mergeKPILists,
} from 'mappings/kpiMapping';
import {
  mapFromKPIDefinitionCommitResult,
  mapFromKPIDefinitionPreview,
  mapFromKPIDefinitionRevisionInfos,
  mapToKPIDefinitionCommit,
  mapToKPIDefinitionPreviewRequest,
  mapToKPIDefinitionTextCorrectionMutation,
  mapToKPIStableMetadataMutation,
} from 'mappings/kpiDefinitionRevisionMapping';
import { mapFromTask } from 'mappings/taskMapping';
import EntityDTO from 'models/dto/entityDTO';
import IdListDTO from 'models/dto/IdListDTO';
import KPIAlertDTO from 'models/dto/kpi/KPIAlertDTO';
import KPIContextDTO from 'models/dto/kpi/KPIContextDTO';
import KPIDataDTO from 'models/dto/kpi/kpiDataDTO';
import KPIDataTransformDTO from 'models/dto/kpi/kpiDataTransformDTO';
import KPIDataTransformLogDTO from 'models/dto/kpi/kpiDataTransformLogDTO';
import KPIDataTransformResponseDTO from 'models/dto/kpi/kpiDataTransformResponseDTO';
import KPIDTO from 'models/dto/kpi/kpiDTO';
import {
  KPIDefinitionCommitResultDTO,
  KPIDefinitionPreviewDTO,
  KPIDefinitionRevisionInfoDTO,
} from 'models/dto/kpi/kpiDefinitionRevisionDTO';
import { FormKPIPlacementResultDTO, TextCorrectionImpactPreviewDTO } from 'models/dto/tasks/formVersionDTO';
import TaskDTO from 'models/dto/tasks/taskDTO';
import Entity from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import KPI, { KPIGraphDataConfig } from 'models/kpi/kpi';
import KPIAlert from 'models/kpi/KPIAlert';
import KPIContext from 'models/kpi/KPIContext';
import KPIData from 'models/kpi/kpiData';
import KPIDataTransform from 'models/kpi/kpiDataTransform';
import KPIDataTransformLog from 'models/kpi/kpiDataTransformLog';
import KPIDataTransformResponse from 'models/kpi/kpiDataTransformResponse';
import {
  KPIDefinitionCommitResult,
  KPIDefinitionPreview,
  KPIDefinitionRevisionInfo,
  KPIDefinitionTextCorrectionMutation,
} from 'models/kpi/kpiDefinitionRevision';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { FormKPIPlacementResult, TextCorrectionImpactPreview } from 'models/tasks/formVersion';
import Task from 'models/tasks/task';
import {
  mapFromKPIDataReportDownload,
  mapFromKPIDataReportEstimate,
  mapFromKPIDataReportJob,
  mapToKPIDataReportRequest,
} from 'mappings/kpiDataReportMapping';
import {
  KPIDataReportDownloadDTO,
  KPIDataReportEstimateDTO,
  KPIDataReportJobDTO,
} from 'models/dto/kpi/kpiDataReportDTO';
import {
  KPIDataReportDownload,
  KPIDataReportEstimate,
  KPIDataReportFormat,
  KPIDataReportJob,
} from 'models/kpi/kpiDataReport';
import { mapFromCopilotFileLibrarySaveResult } from 'mappings/copilotMapping';
import { mapFromReportSaveLocation, mapToReportSaveRequest } from 'mappings/reportSaveLocationMapping';
import { CopilotFileLibrarySaveResult } from 'models/copilot/copilotFileBlock';
import { CopilotFileLibrarySaveResultDTO } from 'models/dto/copilot/copilotFileBlockDTO';
import { ReportSaveLocationDTO } from 'models/dto/library/reportSaveLocationDTO';
import { ReportSaveLocation, ReportSaveRequest } from 'models/library/reportSaveLocation';
import AppError from 'utils/appError';
import { newGuid } from 'utils/guid';
import http from './httpService';

export async function apiGetKPI(
  kpiId: number,
  accessToken: string,
  cache: GlobalDataCache | undefined,
): Promise<KPI | undefined> {
  try {
    if (!kpiId || kpiId <= 0) return undefined;
    const ar = await http.get<KPIDTO>(`/kpis/${kpiId}`, http.getRequestConfig(accessToken));
    if (!ar.data) return undefined;

    return mapFromKPI(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIWithData(
  kpiId: number,
  config: KPIGraphDataConfig,
  accessToken: string,
  cache: GlobalDataCache | undefined,
): Promise<KPI> {
  try {
    const configDto = mapToKPIGraphDataConfig(config);
    const ar = await http.post<KPIDTO>(`/kpis/${kpiId}/data`, configDto, http.getRequestConfig(accessToken));

    return mapFromKPI(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIData(kpi: KPI, kpiDataId: number, accessToken: string): Promise<KPIData | undefined> {
  try {
    const ar = await http.get<KPIDataDTO>(`/kpis/${kpi.kpiId}/data/${kpiDataId}`, http.getRequestConfig(accessToken));
    if (!ar.data) return undefined;

    return mapFromKPIDataPoint(ar.data, kpi);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteKPIData(kpiId: number, ids: number[], accessToken: string): Promise<void> {
  try {
    const idList = new IdListDTO(ids.map((i) => i.toString()));
    await http.post(`/kpis/${kpiId}/data/delete`, idList, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIs(accessToken: string, cache: GlobalDataCache | undefined): Promise<KPI[]> {
  try {
    const ar = await http.get<KPIDTO[]>(`/kpis`, http.getRequestConfig(accessToken));

    return mapFromKPIs(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIFields(accessToken: string, cache: GlobalDataCache | undefined): Promise<KPI[]> {
  try {
    const ar = await http.get<KPIDTO[]>(`/kpis/fields`, http.getRequestConfig(accessToken));

    return mapFromKPIs(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// The full reuse pool for the form editors: promoted KPIs plus the fields that live on forms,
// so a duplicate name resolves to a reuse suggestion instead of the 'exists but not visible' dead end.
export async function apiGetKPIsAndFields(accessToken: string, cache: GlobalDataCache | undefined): Promise<KPI[]> {
  const [kpis, fields] = await Promise.all([apiGetKPIs(accessToken, cache), apiGetKPIFields(accessToken, cache)]);

  return mergeKPILists(kpis, fields);
}

export async function apiAddKPI(kpi: KPI, accessToken: string, cache: GlobalDataCache): Promise<KPI> {
  try {
    const kPIDTO = mapToKPI(kpi);
    const ar = await http.post<KPIDTO>(`/kpis`, kPIDTO, http.getRequestConfig(accessToken));

    return mapFromKPI(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateKPIWithFormPlacement(
  kpi: KPI,
  taskType: TaskType,
  formElements: TaskTypeFormElement[],
  newFieldIndex: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<FormKPIPlacementResult> {
  try {
    const mutation = mapToFormKPIPlacementMutation(kpi, taskType, formElements, newFieldIndex, newGuid(), newGuid());
    const ar = await http.post<FormKPIPlacementResultDTO>(
      '/tasks/forms/kpis',
      mutation,
      http.getRequestConfig(accessToken),
    );

    return mapFromFormKPIPlacementResult(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveKPI(kpiId: number, accessToken: string): Promise<boolean> {
  try {
    await http.post<KPIDTO>(`/kpis/delete/${kpiId}`, null, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateKPI(kpi: KPI, accessToken: string, cache: GlobalDataCache): Promise<KPI> {
  try {
    const kPIDTO = mapToKPI(kpi);
    const ar = await http.put<KPIDTO>(`/kpis`, kPIDTO, http.getRequestConfig(accessToken));

    return mapFromKPI(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiPreviewKPIDefinition(kpi: KPI, accessToken: string): Promise<KPIDefinitionPreview> {
  try {
    const request = mapToKPIDefinitionPreviewRequest(kpi, newGuid());
    const ar = await http.post<KPIDefinitionPreviewDTO>(
      '/kpis/definition/preview',
      request,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDefinitionPreview(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCommitKPIDefinition(
  kpi: KPI,
  previewToken: string,
  reason: string | undefined,
  confirmed: boolean,
  accessToken: string,
): Promise<KPIDefinitionCommitResult> {
  try {
    const request = mapToKPIDefinitionCommit(kpi, previewToken, newGuid(), reason, confirmed);
    const ar = await http.put<KPIDefinitionCommitResultDTO>(
      '/kpis/definition',
      request,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDefinitionCommitResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIDefinitionRevisions(
  kpiId: number,
  accessToken: string,
): Promise<KPIDefinitionRevisionInfo[]> {
  try {
    const ar = await http.get<KPIDefinitionRevisionInfoDTO[]>(
      `/kpis/${kpiId}/definitions`,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDefinitionRevisionInfos(ar.data ?? []);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateKPIStableMetadata(kpi: KPI, accessToken: string): Promise<KPI> {
  try {
    const request = mapToKPIStableMetadataMutation(kpi, newGuid());
    const ar = await http.put<KPIDTO>('/kpis/metadata', request, http.getRequestConfig(accessToken));

    return mapFromKPI(ar.data, undefined);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCorrectKPIDefinitionText(
  mutation: KPIDefinitionTextCorrectionMutation,
  accessToken: string,
  mutationId = newGuid(),
): Promise<void> {
  try {
    const request = mapToKPIDefinitionTextCorrectionMutation(mutation, mutationId);
    await http.post('/kpis/definition/correct', request, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiPreviewKPIDefinitionTextCorrection(
  mutation: KPIDefinitionTextCorrectionMutation,
  accessToken: string,
  mutationId: string,
): Promise<TextCorrectionImpactPreview> {
  try {
    const request = mapToKPIDefinitionTextCorrectionMutation(mutation, mutationId);
    const response = await http.post<TextCorrectionImpactPreviewDTO>(
      '/kpis/definition/correct/preview',
      request,
      http.getRequestConfig(accessToken),
    );

    return mapFromTextCorrectionImpactPreview(response.data);
  } catch (error) {
    throw AppError.fromApiError(error);
  }
}

export async function apiGetKPIContext(
  kpiId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<KPIContext> {
  try {
    const ar = await http.get<KPIContextDTO>(`/kpis/context/${kpiId}`, http.getRequestConfig(accessToken));

    return mapFromKPIContext(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIWithAlerts(
  kpiId: number,
  accessToken: string,
  cache: GlobalDataCache | undefined,
): Promise<KPI> {
  try {
    const ar = await http.get<KPIDTO>(`/kpis/${kpiId}/alerts`, http.getRequestConfig(accessToken));

    return mapFromKPI(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddKPIAlert(
  kpiAlert: KPIAlert,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<KPIAlert> {
  try {
    const KPIAlertDTO = mapToKPIAlert(kpiAlert);
    const ar = await http.post<KPIAlertDTO>(`/kpis/alerts`, KPIAlertDTO, http.getRequestConfig(accessToken));

    return mapFromKPIAlert(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveKPIAlert(kpiId: number, kpiAlertId: number, accessToken: string): Promise<boolean> {
  try {
    await http.post(`/kpis/${kpiId}/alerts/${kpiAlertId}/delete/`, null, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateKPIAlert(
  kpiAlert: KPIAlert,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<KPIAlert> {
  try {
    const KPIAlertDTO = mapToKPIAlert(kpiAlert);
    const ar = await http.put<KPIAlertDTO>(`/kpis/alerts`, KPIAlertDTO, http.getRequestConfig(accessToken));

    return mapFromKPIAlert(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateKPIData(data: KPIData, accessToken: string): Promise<void> {
  try {
    const kpiDataDto = mapToKPIData([data]);
    await http.put(`/kpis/data`, kpiDataDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIDataTransform(
  kpiId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<KPIDataTransform | undefined> {
  try {
    const ar = await http.get<KPIDataTransformDTO>(`/kpis/${kpiId}/transform`, http.getRequestConfig(accessToken));

    return mapFromKPIDataTransform(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateKPIDataTransform(
  kpiDataTransform: KPIDataTransform,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<KPIDataTransform | undefined> {
  try {
    const kpiDataTransformDto = mapToKPIDataTransform(kpiDataTransform);
    const ar = await http.put<KPIDataTransformDTO>(
      `/kpis/transform`,
      kpiDataTransformDto,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDataTransform(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiApplyKPIDataTransform(
  kpiDataTransform: KPIDataTransform,
  accessToken: string,
): Promise<KPIDataTransformResponse[]> {
  try {
    const kpiDataTransformDto = mapToKPIDataTransform(kpiDataTransform);
    const ar = await http.post<KPIDataTransformResponseDTO[]>(
      `/kpis/data/transform`,
      kpiDataTransformDto,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDataTransformResponses(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiTestKPINotAcceptedEvidenceEvent(
  kpiDataTransform: KPIDataTransform,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Task | undefined> {
  try {
    const kpiDataTransformDto = mapToKPIDataTransform(kpiDataTransform);
    const ar = await http.post<TaskDTO>(
      `/kpis/data/transform/test`,
      kpiDataTransformDto,
      http.getRequestConfig(accessToken),
    );

    if (ar.data) {
      return mapFromTask(ar.data, cache);
    } else {
      return undefined;
    }
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIDataTransformLogs(kpiId: number, accessToken: string): Promise<KPIDataTransformLog[]> {
  try {
    const ar = await http.get<KPIDataTransformLogDTO[]>(
      `/kpis/${kpiId}/transform/logs`,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDataTransformLogs(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteKPITransformLogs(
  kpiId: number,
  kpiDataTransformLogIds: number[],
  accessToken: string,
): Promise<void> {
  try {
    const idListDto = new IdListDTO(kpiDataTransformLogIds.map((s) => s.toString()));
    await http.post(`/kpis/${kpiId}/transform/logs/delete`, idListDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiReprocessKPITransformLogs(
  kpiDataTransformLogIds: number[],
  accessToken: string,
): Promise<void> {
  try {
    const idListDto = new IdListDTO(kpiDataTransformLogIds.map((s) => s.toString()));
    await http.post(`/kpis/transform/logs/reprocess`, idListDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIUsage(kpiId: number, accessToken: string): Promise<Entity[]> {
  try {
    const ar = await http.get<EntityDTO[]>(`/kpis/${kpiId}/usage`, http.getRequestConfig(accessToken));

    return mapFromEntities(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIName(name: string, accessToken: string): Promise<number> {
  try {
    const ar = await http.post<number>(`/kpis/name`, { valueString: name }, http.getRequestConfig(accessToken));
    const id = Number(ar.data);

    return id;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//
// The KPI data report (PLAN-064): the estimate, the job and the download link
//
export async function apiGetKPIDataReportEstimate(
  kpiIds: number[],
  start: Date,
  end: Date,
  accessToken: string,
): Promise<KPIDataReportEstimate> {
  try {
    const ar = await http.post<KPIDataReportEstimateDTO>(
      `/kpis/data-report/estimate`,
      mapToKPIDataReportRequest(kpiIds, start, end),
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDataReportEstimate(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//429 (code '429') when the organization already holds its maximum of queued reports; the message is the API's text
export async function apiStartKPIDataReportJob(
  kpiIds: number[],
  start: Date,
  end: Date,
  format: KPIDataReportFormat,
  accessToken: string,
): Promise<KPIDataReportJob> {
  try {
    const ar = await http.post<KPIDataReportJobDTO>(
      `/kpis/data-report/jobs`,
      mapToKPIDataReportRequest(kpiIds, start, end, format),
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDataReportJob(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//the user's newest job that is queued, running, or done with its file still there; undefined when there is none
export async function apiGetActiveKPIDataReportJob(accessToken: string): Promise<KPIDataReportJob | undefined> {
  try {
    const ar = await http.get<KPIDataReportJobDTO>(`/kpis/data-report/jobs/active`, http.getRequestConfig(accessToken));
    if (ar.status === 204 || !ar.data) return undefined;

    return mapFromKPIDataReportJob(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetKPIDataReportJob(jobId: number, accessToken: string): Promise<KPIDataReportJob> {
  try {
    const ar = await http.get<KPIDataReportJobDTO>(
      `/kpis/data-report/jobs/${jobId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDataReportJob(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//a new read-only link to the file on every call (fifteen minutes); 410 (code '410') when the file is gone
export async function apiGetKPIDataReportDownload(jobId: number, accessToken: string): Promise<KPIDataReportDownload> {
  try {
    const ar = await http.get<KPIDataReportDownloadDTO>(
      `/kpis/data-report/jobs/${jobId}/download`,
      http.getRequestConfig(accessToken),
    );

    return mapFromKPIDataReportDownload(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//the default location of a finished job's file (PLAN-069), resolved without touching the provider
export async function apiGetKPIDataReportSaveLocation(jobId: number, accessToken: string): Promise<ReportSaveLocation> {
  try {
    const ar = await http.get<ReportSaveLocationDTO>(
      `/kpis/data-report/jobs/${jobId}/save/location`,
      http.getRequestConfig(accessToken),
    );

    return mapFromReportSaveLocation(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//saves a finished job's file to the library: the chosen category and folder, or the default location of the purpose;
//a saved job answers the same item again. A gone start folder of the default answers OUTPUTLOC0009 in AppError.data.code
export async function apiSaveKPIDataReportToLibrary(
  jobId: number,
  request: ReportSaveRequest,
  accessToken: string,
): Promise<CopilotFileLibrarySaveResult> {
  try {
    const ar = await http.post<CopilotFileLibrarySaveResultDTO>(
      `/kpis/data-report/jobs/${jobId}/save`,
      mapToReportSaveRequest(request),
      http.getRequestConfig(accessToken),
    );

    return mapFromCopilotFileLibrarySaveResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
